import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { isEmpty } from 'lodash';
import {
  customHandleSaveAsset,
  customHandleSaveAttachement,
  getImageUrl,
  handleSaveAsset,
  handleSaveAttachement,
  showImage,
} from 'utils/helper';
import { showImageError } from 'containers/blog/helper';
import Loader from 'components/Loader';
import { API_BASE_URL } from 'utils/constants';
import { api } from 'utils/axios';
import AuthService from 'common/auth.service';

const UploadMultipleImages = ({
  forType,
  formik,
  fieldName,
  label,
  showOriginalInput = false,

  renameOriginal = true,
  renameOriginalRaw = true,
  isAsset = true,
  isHealthcare = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(false);
  const [renameOriginalInput, setRenameOriginalInput] = useState(false);

  const fieldValue = formik?.values?.[fieldName] || [];

  const auth = new AuthService();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];

    selectedFiles.forEach((file) => {
      const filename = file.name;
      const [baseFilename, ...extensionParts] = filename.split('.');
      const extension = extensionParts.length > 0 ? '.' + extensionParts.join('.') : '';
      const validFilenameRegex = /^[a-zA-Z0-9-_]+$/;

      if (!validFilenameRegex.test(baseFilename)) {
        const updatedBaseFilename = baseFilename
          .toLowerCase()
          .replace(/[^a-zA-Z0-9-_]/g, '_')
          .replace(/^[-_]+|[-_]+$/g, '');
        const updatedFileName = updatedBaseFilename + extension;

        alert(`Invalid filename. Use this filename: ${updatedFileName}`);
        setImageUploadError(true);
      } else {
        validFiles.push(file);
      }
    });

    if (validFiles.length > 0) {
      setFiles(validFiles);
      setImageUploadError(false);
    }
  };

  const handleUpload = async () => {
    if (!isEmpty(files)) {
      setShowLoading(true);

      const payload = {
        isAsset: true,
        renameOriginal: showOriginalInput ? renameOriginalInput : renameOriginal,
        renameOriginalRaw,
      };

      const customHeaders = isHealthcare
        ? {
            Authorization: `Bearer ${auth.getSessionCookie()}`,
            'x-api-key': process.env.REACT_APP_HEALTHCARE_API_KEY,
          }
        : {};

      try {
        const uploadedResults = [];

        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);

          const apiConfig = {
            method: 'POST',
            url: `${API_BASE_URL}/attachment/upload?config=${btoa(JSON.stringify(payload))}`,
            data: formData,
          };

          let responseFile;
          if (isHealthcare) {
            responseFile = await api(apiConfig, customHeaders);

            if (isAsset) {
              const saved = await customHandleSaveAsset(responseFile, forType, customHeaders);
              uploadedResults.push(saved);
            } else {
              const saved = await customHandleSaveAttachement(responseFile, forType, customHeaders);
              uploadedResults.push(saved);
            }
          } else {
            responseFile = await api(apiConfig);
            if (isAsset) {
              const saved = await handleSaveAsset(responseFile, forType);
              uploadedResults.push(saved);
            } else {
              const saved = await handleSaveAttachement(responseFile, forType);
              uploadedResults.push(saved);
            }
          }
        }

        toggleModal();
        formik.setFieldValue(fieldName, uploadedResults);
      } catch (err) {
        console.warn('Error uploading files:', err);
      } finally {
        setShowLoading(false);
        setFiles([]);
      }
    }
  };

  const handleRemove = async (index) => {
    const result = await confirm({
      title: 'Remove Image',
      message: 'Are you sure you want to remove this image?',
      confirmText: 'Yes',
      cancelText: 'No',
    });

    if (result) {
      const updatedImages = [...fieldValue];
      updatedImages.splice(index, 1);
      formik.setFieldValue(fieldName, updatedImages);
    }
  };

  return (
    <>
      <Loader isActive={showLoading} />
      <Card className="border">
        <CardBody>
          <CardTitle>{label}</CardTitle>

          <Button className="w-100 py-3 mt-2" outline color="dark" onClick={toggleModal}>
            Add Images
          </Button>

          <Modal centered={true} size="lg" isOpen={modalOpen} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal}>Select and Upload Images</ModalHeader>
            <ModalBody>
              {showOriginalInput && (
                <FormGroup className={'mb-4 form-group border p-3 rounded-2 border-secondary'}>
                  <Label className="mb-2">is this image and unedited version of image from third party source ?</Label>
                  <div className="d-flex align-items-center">
                    {[true, false].map((option, i) => (
                      <div key={`renameOriginalInput_${i}`} className="form-check d-inline-block me-4">
                        <input
                          type="radio"
                          id={`renameOriginalInput_${i}`}
                          className="form-check-input"
                          checked={renameOriginalInput === option}
                          onChange={() => setRenameOriginalInput(option)}
                        />
                        <label className="form-check-label" htmlFor={`renameOriginalInput_${i}`}>
                          {option ? 'Yes' : 'No'}
                        </label>
                      </div>
                    ))}
                  </div>
                </FormGroup>
              )}

              <input type="file" accept="image/*" multiple onChange={handleFileChange} />

              {!isEmpty(files) && (
                <div className="row mx-0 mt-4">
                  {files?.map((file, index) => (
                    <div className="col-md-6" key={index}>
                      <div className="border rounded-2 p-3 text-center mb-4">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${index}`}
                          style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                        />
                        <h6 className="mb-0 mt-2">{file?.name || `Image ${index + 1}`}</h6>
                        <button
                          type="button"
                          className="btn btn-outline-danger mt-3 w-100"
                          onClick={() => {
                            setFiles((prev) => prev.filter((_, i) => i !== index));
                          }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                className="me-3"
                disabled={imageUploadError || isEmpty(files)}
                onClick={handleUpload}>
                Start Upload
              </Button>
              <Button color="light" onClick={toggleModal}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          {fieldValue?.length > 0 && (
            <div className="mt-4 ">
              {fieldValue?.map((img, index) => (
                <div key={index} className="border rounded-2 p-3 text-center mb-4">
                  <img
                    src={typeof img === 'string' ? showImage(img) : getImageUrl(img)}
                    alt={`uploaded-${index}`}
                    className="img-fluid p-1"
                    style={{ maxHeight: '100px' }}
                    onError={showImageError}
                  />
                  <h6 className="mb-0 mt-2">{img?.name || `Image ${index + 1}`}</h6>
                  <button
                    type="button"
                    className="btn btn-outline-danger mt-3 w-100"
                    onClick={() => {
                      handleRemove(index);
                    }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <FormFeedback className="d-block">{formik.errors[fieldName]}</FormFeedback>
        </CardBody>
      </Card>
    </>
  );
};

export default UploadMultipleImages;
